import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useBudgets } from 'src/hooks/queries/budgets';
import { useUser } from 'src/hooks/queries/users';
import { useHistory, useLocation } from 'react-router';
import { UserUtils } from 'src/types/user.model';
import styles from './spendingBudget.module.scss';
import React, { useLayoutEffect } from 'react';
import CategoryCardGrid from 'src/components/atomic/organisms/categoryCardGrid/CategoryCardGrid';
import BudgetStatsBox from 'src/components/atomic/organisms/budgetStatsBox/categoryCardRefresh/BudgetStatsBox';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import colors from 'src/styles/colors.module.scss';
import Selectors from 'src/utils/selectors';
import LiteralsService from 'src/services/literals/LiteralsService';
import BudgetEditButton from 'src/components/atomic/atoms/budgetEditButton/BudgetEditButton';
import LinkExternalAccountBody from 'src/components/atomic/molecules/linkExternalAccountBody/LinkExternalAccountBody';
import { useIsFetching } from '@tanstack/react-query';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { useCategories } from 'src/hooks/queries/categories';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import { useSpendingSums } from 'src/hooks/queries/spending';
import DateUtils from 'src/utils/date';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import H from 'history';
import { BudgetModel } from 'src/types/budget.model';
import { CategoryModel } from 'src/types/category.model';
import { SpendingSumModel } from 'src/types/spending-sum.model';
import { getBudgetForCategory, getCategoryBudgets } from 'src/utils/budget.utils';
import LinkExternalAccountCallout, {
    LinkExternalAccountCalloutVersions
} from 'src/components/atomic/molecules/linkExternalAccountCallout/LinkExternalAccountCallout';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { IconCardSwipe } from '@americanexpress/dls-icons';
import EmptyChartState from 'src/components/atomic/atoms/emptyChartState/EmptyChartState';
import { groupByCategory } from 'src/utils/category.utils';
import { WalkmeName, WalkmePopupWrapper } from '../../organisms/walkme';

export default function SpendingBudgetWrapper({ timePeriod }: { timePeriod: PageTimePeriod }): JSX.Element {
    const [searchParams, setSearchParams] = useSearchParams();
    const titleId: string = `spendingBudgetTitle-${timePeriod}`;
    const { isMinWidthSmall, isMinWidthMedium } = useBreakpoints();
    const { isLoading: isLoadingSpendingSums, data: spendingSums } = useSpendingSums(
        DateUtils.getDatesFromTimePeriod(timePeriod)
    );
    const { isLoading: isLoadingBudgets, data: budgetsData } = useBudgets();
    const budgets: BudgetModel[] = timePeriod === PageTimePeriod.CURRENT_MONTH ? budgetsData : [];
    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { isSyncingAccounts } = useSyncingAccounts();
    const { data: user } = useUser();
    const history = useHistory();
    const { pathname } = useLocation();
    const isLoading =
        isLoadingSpendingSums ||
        (timePeriod === PageTimePeriod.CURRENT_MONTH && isLoadingBudgets) ||
        isLoadingCategories;
    const categoryBudgets = getCategoryBudgets(budgets);
    const isFullPlanUser: boolean = !UserUtils.isBudgetingUser(user);
    const isFetching = useIsFetching();

    useLayoutEffect(() => {
        if (isFetching === 0 && categoryBudgets?.length > 0 && searchParams.get('savedBudgets')) {
            document.getElementById(titleId)?.scrollIntoView({ behavior: 'smooth' });
            setSearchParams((prev) => {
                prev.delete('savedBudgets');
                return prev;
            });
        }
    }, [categoryBudgets, isFetching]);

    const familySpendingSums = groupByCategory(spendingSums, categories, (spendingSum) => -spendingSum.spent)
        // filter out parent categories with no spending and no budget
        .filter((e) => e[0].spent || getBudgetForCategory(budgets, e[0].groupId))
        // filter out all children with no spending and no budget
        .map((e) =>
            e.filter(({ spent, groupId }) => spent || getBudgetForCategory(budgets, groupId))
        ) as SpendingSumModel[][];

    return (
        <SpendingBudget
            titleId={titleId}
            familySpendingSums={familySpendingSums}
            budgets={categoryBudgets}
            categories={categories}
            history={history}
            pathname={pathname}
            isLoading={isLoading}
            isSyncingAccounts={isSyncingAccounts}
            isFullPlanUser={isFullPlanUser}
            isMinWidthSmall={isMinWidthSmall}
            isMinWidthMedium={isMinWidthMedium}
            headerText={
                timePeriod === PageTimePeriod.CURRENT_MONTH
                    ? LiteralsService.getLiteral('MonthlySpendingBudget.header')
                    : LiteralsService.getLiteral('SpendingCategories.totalYearlySpending')
            }
        />
    );
}

interface SpendingBudgetProps {
    titleId: string;
    familySpendingSums: SpendingSumModel[][];
    budgets: BudgetModel[];
    categories: CategoryModel[];
    history: H.History;
    pathname: string;
    isLoading?: boolean;
    isSyncingAccounts?: boolean;
    isFullPlanUser?: boolean;
    isMinWidthSmall?: boolean;
    isMinWidthMedium?: boolean;
    headerText: string;
}

export function SpendingBudget({
    titleId,
    familySpendingSums,
    budgets,
    categories,
    history,
    pathname,
    isLoading,
    isSyncingAccounts,
    isFullPlanUser,
    isMinWidthSmall,
    isMinWidthMedium,
    headerText
}: SpendingBudgetProps): JSX.Element {
    const header: JSX.Element = (
        <div className={styles.header} id={titleId} data-cy={Selectors.SpendSmart.MONTHLY_SPENDING_BUDGET_HEADER}>
            <WalkmePopupWrapper walkmeName={WalkmeName.TRANSACTION_CLASSIFICATION} stepIndex={2}>
                <Typography component="h2" variant={TypographyVariants.HEADING_S} className={colors.brandStrong}>
                    {headerText}
                </Typography>
            </WalkmePopupWrapper>
            <BudgetEditButton
                history={history}
                hasBudgets={budgets?.length > 0}
                isFullPlanUser={isFullPlanUser}
                isMinWidthSmall={isMinWidthSmall}
            />
        </div>
    );

    if (!isLoading && (!familySpendingSums || familySpendingSums.length === 0) && (!budgets || budgets.length === 0)) {
        return (
            <div className={styles.emptyState}>
                {header}
                <EmptyChartState
                    icon={<IconCardSwipe size="lg" />}
                    text={LiteralsService.getLiteral('General.grid.noDataText')}
                    subtext={LiteralsService.getLiteral('General.grid.noDataSubtext')}
                    data-cy={Selectors.SpendSmart.EMPTY_CHART}
                />
            </div>
        );
    }

    if (isMinWidthMedium) {
        return (
            <div className={styles.monthlySpendingBudget} data-cy={Selectors.SpendSmart.MONTHLY_SPENDING_BUDGET}>
                <div className={styles.left}>
                    {header}
                    <CategoryCardGrid
                        familySpendingSums={familySpendingSums}
                        budgets={budgets}
                        categories={categories}
                        history={history}
                        isLoading={isLoading}
                        isSyncingAccounts={isSyncingAccounts}
                        isMinWidthSmall={isMinWidthSmall}
                    />
                </div>
                <div className={styles.right}>
                    {isLoading ? (
                        <Loading className={styles.loading} />
                    ) : budgets?.length > 0 ? (
                        <>
                            <BudgetStatsBox
                                budgets={budgets}
                                categories={categories}
                                pathname={pathname}
                                isMinWidthSmall={isMinWidthSmall}
                                isMinWidthMedium={isMinWidthMedium}
                            />
                            <LinkExternalAccountBody history={history} />
                        </>
                    ) : (
                        <LinkExternalAccountCallout
                            version={LinkExternalAccountCalloutVersions.CATEGORY_CARDS_REFRESH}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.monthlySpendingBudget} data-cy={Selectors.SpendSmart.MONTHLY_SPENDING_BUDGET}>
            {header}
            {isLoading ? (
                <>
                    <Loading className={styles.loading} />
                    <CategoryCardGrid
                        familySpendingSums={familySpendingSums}
                        budgets={budgets}
                        categories={categories}
                        history={history}
                        isLoading={isLoading}
                        isSyncingAccounts={isSyncingAccounts}
                        isMinWidthSmall={isMinWidthSmall}
                    />
                </>
            ) : budgets?.length > 0 ? (
                <>
                    <BudgetStatsBox
                        budgets={budgets}
                        categories={categories}
                        pathname={pathname}
                        isMinWidthSmall={isMinWidthSmall}
                        isMinWidthMedium={isMinWidthMedium}
                    />
                    <CategoryCardGrid
                        familySpendingSums={familySpendingSums}
                        budgets={budgets}
                        categories={categories}
                        history={history}
                        isLoading={isLoading}
                        isSyncingAccounts={isSyncingAccounts}
                        isMinWidthSmall={isMinWidthSmall}
                    />
                    <LinkExternalAccountBody history={history} />
                </>
            ) : (
                <>
                    <CategoryCardGrid
                        familySpendingSums={familySpendingSums}
                        budgets={budgets}
                        categories={categories}
                        history={history}
                        isLoading={isLoading}
                        isSyncingAccounts={isSyncingAccounts}
                        isMinWidthSmall={isMinWidthSmall}
                    />
                    <LinkExternalAccountCallout version={LinkExternalAccountCalloutVersions.CATEGORY_CARDS_REFRESH} />
                </>
            )}
        </div>
    );
}
